import { ReactElement, ReactNode } from 'react';
import { Popover as ChakraPopoverNamespace, Portal } from '@chakra-ui/react';

import { CloseButton } from '../CloseButton';
import { LEGACY_MODE, LegacyMode, ModernMode, useMode } from '../Mode';

export interface PopoverContentProps extends ChakraPopoverNamespace.ContentProps {
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement | null>;
  ref?: React.RefObject<HTMLDivElement | null>;
}

export function PopoverContent({ ref, ...props }: PopoverContentProps) {
  const { portalled = true, portalRef, ...rest } = props;
  const { mode } = useMode();
  return (
    <Portal disabled={!portalled} container={portalRef}>
      <ChakraPopoverNamespace.Positioner>
        {mode === LEGACY_MODE ? (
          <LegacyMode>
            <ChakraPopoverNamespace.Content ref={ref} {...rest} />
          </LegacyMode>
        ) : (
          <ModernMode>
            <ChakraPopoverNamespace.Content ref={ref} {...rest} />
          </ModernMode>
        )}
      </ChakraPopoverNamespace.Positioner>
    </Portal>
  );
}

export interface PopoverArrowProps extends ChakraPopoverNamespace.ArrowProps {
  ref?: React.RefObject<HTMLDivElement | null>;
}
export function PopoverArrow({ ref, ...props }: PopoverArrowProps) {
  return (
    <ChakraPopoverNamespace.Arrow {...props} ref={ref}>
      <ChakraPopoverNamespace.ArrowTip />
    </ChakraPopoverNamespace.Arrow>
  );
}

export function PopoverCloseTrigger({
  ref,
  ...props
}: ChakraPopoverNamespace.CloseTriggerProps & {
  ref: React.RefObject<HTMLButtonElement | null>;
}) {
  return (
    <ChakraPopoverNamespace.CloseTrigger position="absolute" top="1" insetEnd="1" {...props} asChild ref={ref}>
      <CloseButton size="sm" />
    </ChakraPopoverNamespace.CloseTrigger>
  );
}

export const PopoverTitle = ChakraPopoverNamespace.Title;
export const PopoverDescription = ChakraPopoverNamespace.Description;
export const PopoverFooter = ChakraPopoverNamespace.Footer;

export interface PopoverHeaderProps extends ChakraPopoverNamespace.HeaderProps {
  ref?: React.RefObject<HTMLDivElement | null>;
}
export function PopoverHeader({ ref, ...props }: PopoverHeaderProps) {
  return <ChakraPopoverNamespace.Header as="h2" {...props} ref={ref} />;
}
export const PopoverRoot = ChakraPopoverNamespace.Root;
export const PopoverBody = ChakraPopoverNamespace.Body;
export const PopoverTrigger = ChakraPopoverNamespace.Trigger;

export interface PopoverProps {
  /**
   * Element which will be used to open the popover's content.
   *
   * Must be a single interactive element (e.g. a button or link)
   */
  trigger: ReactElement;
  /**
   * Header for the popover
   */
  header?: ReactNode;
  /**
   * Additional props to be passed to the popover content container
   */
  contentProps?: Omit<PopoverContentProps, 'children'>;
  /**
   * Body content for the popover
   */
  body: ReactNode;
}

export function Popover({ trigger, header, contentProps, body }: PopoverProps) {
  return (
    <PopoverRoot lazyMount>
      <PopoverTrigger>{trigger}</PopoverTrigger>
      <Portal>
        <PopoverContent {...contentProps}>
          <PopoverArrow />
          {header && <PopoverHeader>{header}</PopoverHeader>}
          <PopoverBody>{body}</PopoverBody>
        </PopoverContent>
      </Portal>
    </PopoverRoot>
  );
}
